import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  Output,
  OnChanges,
  OnDestroy,
  QueryList,
  SimpleChanges,
  ViewChildren,
  EventEmitter,
  ViewChild,
} from '@angular/core';
import { FacetChipViewModel } from '@app/new-search/models/facet-filter.model';
import { TranslateService } from '@ngx-translate/core';
import { ChipTypeEnum } from '@app/shared-product/components/usf-chip/models/chip-type.enum';
import { Subscription } from 'rxjs';
import {
  ListFacetSharedObject,
  ListFacetValue,
} from '@app/new-search/models/new-search-facets.model';

@Component({
  selector: 'app-new-search-facet-pills',
  templateUrl: './new-search-facet-pills.component.html',
  styleUrls: ['./new-search-facet-pills.component.scss'],
})
export class NewSearchFacetPillsComponent
  implements AfterViewInit, OnDestroy, OnChanges
{
  static ADDITIONAL_FACETS_WIDTH = 80; // px
  static FACET_MARGIN_WIDTH = 11; //px

  @Input() facetChipViewModels: FacetChipViewModel[] = [];
  @Input() showOverflowButton = false;
  @Input() listFacet: ListFacetSharedObject = {} as ListFacetSharedObject;

  @Output() additionalFacetsClick: EventEmitter<any> = new EventEmitter();
  @Output() chipMyListSelected = new EventEmitter<void>();

  @ViewChildren('NewSearchFacetChip', { read: ElementRef })
  facetchips: QueryList<ElementRef>;

  @ViewChild('chipContainer', { read: ElementRef })
  chipContainer: ElementRef;

  readonly chipTypeEnum = ChipTypeEnum;

  facetChipsToShow: FacetChipViewModel[] = [];
  numberOfExtraFacets = 0;

  facetChipsOberver$: Subscription;
  isLoading = false;

  additionalFacetsMessage = '';
  MORE = 'i18n.search.more';

  constructor(
    private el: ElementRef,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
  ) {}

  getTotalFacets(): number {
    const listFacetCount = this.listFacet?.values
      ? [...this.listFacet.values.values()].filter(v => v.isSelected).length
      : 0;
    return this.facetChipViewModels.length + listFacetCount;
  }

  isListFacetValue(facetValue: any): facetValue is ListFacetValue {
    return facetValue && 'id' in facetValue && 'isSelected' in facetValue;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.isLoading = true;
    // Create a combined array of regular facets and list facets
    const regularFacets = changes.facetChipViewModels?.currentValue || [];
    const listFacets = this.listFacet?.values
      ? Array.from(this.listFacet.values.values())
          .filter(value => value.isSelected)
          .map(value => ({
            facet: this.listFacet,
            facetValue: value,
          }))
      : [];

    this.facetChipsToShow = [...listFacets, ...regularFacets];
  }

  // when the facetChipViewModels array is updated, onChanges will trigger and
  // cause facetchips to emit a change as well
  // for each facet value check to see if width used > container width
  // if the number of facet values > 8 OR container width is exceeded,
  // slice array to fit container and show +X button
  ngAfterViewInit(): void {
    if (this.isLoading === true && this.facetchips.length > 0) {
      const facetElementsArray = this.facetchips.toArray();
      let widthUsed = 0;
      let indexToTrim = 0;

      // Calculate width for all chips
      for (const facet of facetElementsArray) {
        const chipWidth = facet.nativeElement.getBoundingClientRect().width;
        widthUsed +=
          chipWidth + NewSearchFacetPillsComponent.FACET_MARGIN_WIDTH;

        // Check if we've exceeded the container width
        if (
          widthUsed + NewSearchFacetPillsComponent.ADDITIONAL_FACETS_WIDTH >
            this.chipContainer.nativeElement.offsetWidth &&
          indexToTrim === 0
        ) {
          indexToTrim = facetElementsArray.indexOf(facet);
        }
      }

      const totalFacets = this.getTotalFacets();

      // Only apply the 8-facet limit if we haven't already hit the width limit
      if (totalFacets > 8 && indexToTrim === 0) {
        indexToTrim = 8;
      }

      if (indexToTrim > 0) {
        this.numberOfExtraFacets = totalFacets - indexToTrim;
        this.facetChipsToShow = this.facetChipsToShow.slice(0, indexToTrim);
        this.additionalFacetsMessage = `+${totalFacets - indexToTrim} ${this.translateService.instant(this.MORE)}`;
        this.cdr.detectChanges();
      }

      this.isLoading = false;
    }
  }

  ngOnDestroy(): void {
    this.facetChipsOberver$?.unsubscribe();
  }

  handleChipClick($event: FacetChipViewModel) {
    if ($event.facet.type === 'regular') {
      $event.facet.toggleSelect($event.facetValue);
    } else if ($event.facet.type === 'hierarchical') {
      $event.facet.deselectAll();
    }
  }

  handleAdditionalButtonClick() {
    this.additionalFacetsClick.emit();
  }

  handleListChipClick(listFacetValue: ListFacetValue) {
    this.listFacet.values.get(listFacetValue.id).isSelected = false;
    this.listFacet.isValuesSelected = this.checkIsListFaceSelected();
    this.chipMyListSelected.emit();
  }

  checkIsListFaceSelected(): boolean {
    let val = false;
    this.listFacet.values.forEach((value, key) => {
      if (value.isSelected) {
        val = true;
      }
    });
    return val;
  }
}
